'use client'

import {
  canEditClaim,
  getDisabledClaimTooltip,
  getDisabledStatusesFromPermissions,
  getDisabledStatusesFromCurrentStatuses,
  mergeDisabledStatuses
} from '@repo/utils/claimPermissions'
import { Avatar } from '@repo/ui/Avatar'
import { Chat } from '@repo/ui/Chat'
import { ClaimStatusDropdownButton } from '@repo/ui/ClaimStatusDropdownButton'
import { ClientNameAndMatterDescription } from '@repo/ui/ClientNameAndMatterDescription'
import { EditIcon } from '@repo/ui/EditIcon'
import { formatCurrency } from '@repo/utils/numbers'
import { formatDate, getRelativeDate } from '@repo/utils/time'
import { LoadingState } from '@repo/ui/LoadingState'
import { ArrowRightIcon } from '@repo/ui/ArrowRightIcon'
import { useAuditHistoryStore } from '@repo/store/useAuditHistoryStore'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { useClaimsStore } from '@repo/store/useClaimsStore'
import { useEffect, useMemo, useState } from 'react'
import { useCommentsStore } from '@repo/store/useCommentsStore'
import { useOvertimesStore } from '@repo/store/useOvertimesStore'
import { useToastStore } from '@repo/store/useToastStore'
import { useMenuItemsStore } from '@repo/store/useMenuItemsStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import MUIBox from '@mui/material/Box'
import MUIButton from '@mui/material/Button'
import MUIDivider from '@mui/material/Divider'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'

export const OvertimeReviewModule = () => {
  const { fetchOvertimeDetails, updateOvertimeClaimStatuses, overtimesTableUpdate } = useOvertimesStore()
  const { fetchOvertimeClaimAuditHistoryByOvertimeClaimId } = useAuditHistoryStore()
  const { userId } = useAuthenticationStore()
  const {
    comments,
    createOvertimeClaimComment,
    updateOvertimeClaimComment,
    deleteOvertimeClaimComment,
    fetchAllOvertimeClaimCommentsByOvertimeClaimId
  } = useCommentsStore()
  const { isClaimsDrawerOpen, setIsClaimsDrawerOpen, fetchAllOvertimeUserPermissionsByProcessorUserId } =
    useClaimsStore()
  const { allowedActions } = useMenuItemsStore()
  const { submitOvertimeClaims, processOvertimeClaims } = allowedActions
  const { fetchMatter } = useMattersStore()
  const navigate = useNavigate()
  const theme = useTheme()
  const location = useLocation()

  const [matterDetails, setMatterDetails] = useState<any>(null)
  const [overtimeDetails, setOvertimeDetails] = useState<any>(null)
  const [claimStatus, setClaimStatus] = useState<number | null>(location.state?.status ?? null)
  const [claimStatusText, setClaimStatusText] = useState<string>('')
  const [auditHistory, setAuditHistory] = useState<any[]>([])
  const [commentsLoading, setCommentsLoading] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [permissions, setPermissions] = useState<any[]>([])
  const [permissionsLoading, setPermissionsLoading] = useState(true)

  const overtimeClaimId = location.pathname.split('/').pop() || null
  const isEditDisabled = !canEditClaim(false, claimStatus)

  useEffect(() => {
    if (!userId) return

    let cancelled = false
    setPermissionsLoading(true)

    fetchAllOvertimeUserPermissionsByProcessorUserId({ processorUserId: userId })
      .then((result: any) => {
        if (!cancelled) setPermissions(Array.isArray(result) ? result : [])
      })
      .catch((error: any) => {
        console.error('Error fetching overtime user permissions:', error)
        if (!cancelled) setPermissions([])
      })
      .finally(() => {
        if (!cancelled) setPermissionsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [userId, fetchAllOvertimeUserPermissionsByProcessorUserId])

  const { disabledStatuses, tooltips: statusTooltips } = useMemo(() => {
    let permResult
    if (permissionsLoading || !overtimeDetails?.claimRecipientUserId) {
      permResult = getDisabledStatusesFromPermissions([], '')
    } else if (permissions.length === 0) {
      const disabledStatuses = [0]
      const tooltips: Record<number, string> = { 0: 'Claims cannot be changed to draft status' }

      if (!submitOvertimeClaims?.allowed) {
        ;[1, 2, 3, 4].forEach((s) => {
          disabledStatuses.push(s)
          tooltips[s] = 'You do not have permission to approve claims'
        })
      }

      if (!processOvertimeClaims?.allowed) {
        ;[5, 6, 7, 8].forEach((s) => {
          disabledStatuses.push(s)
          tooltips[s] = 'You do not have permission to process payments'
        })
      }

      permResult = { disabledStatuses, tooltips }
    } else {
      permResult = getDisabledStatusesFromPermissions(permissions, overtimeDetails.claimRecipientUserId)
    }

    const statusResult =
      claimStatus !== null && claimStatus !== undefined
        ? getDisabledStatusesFromCurrentStatuses([claimStatus])
        : { disabledStatuses: [] as number[], tooltips: {} as Record<number, string> }

    return mergeDisabledStatuses(permResult, statusResult)
  }, [permissions, permissionsLoading, overtimeDetails?.claimRecipientUserId, claimStatus, submitOvertimeClaims?.allowed, processOvertimeClaims?.allowed])

  useEffect(() => {
    if (!overtimeClaimId) return

    const getOvertimeDetails = async () => {
      try {
        const response = await fetchOvertimeDetails({ overtimeClaimId })
        setOvertimeDetails(response)
        if (response?.status !== undefined) setClaimStatus(response.status)
      } catch (error) {
        console.error('Error fetching overtime details:', error)
      }
    }

    getOvertimeDetails()
  }, [overtimeClaimId, fetchOvertimeDetails, overtimesTableUpdate])

  useEffect(() => {
    if (isClaimsDrawerOpen || !overtimeClaimId) return

    setCommentsLoading(true)
    fetchAllOvertimeClaimCommentsByOvertimeClaimId({ overtimeClaimId }).finally(() => {
      setCommentsLoading(false)
    })
    fetchOvertimeDetails({ overtimeClaimId }).then((response: any) => {
      if (response) {
        setOvertimeDetails(response)
        if (response.status !== undefined) setClaimStatus(response.status)
      }
    })
  }, [isClaimsDrawerOpen])

  useEffect(() => {
    if (!overtimeDetails?.clientNo || !overtimeDetails?.matterNo) return

    const clientMatterNo = `${overtimeDetails.clientNo}-${overtimeDetails.matterNo}`
    fetchMatter({ clientMatterNo }).then((matter: any) => {
      if (matter) setMatterDetails(matter)
    })
  }, [overtimeDetails?.clientNo, overtimeDetails?.matterNo, fetchMatter])

  useEffect(() => {
    if (!overtimeClaimId) return

    setCommentsLoading(true)
    fetchAllOvertimeClaimCommentsByOvertimeClaimId({ overtimeClaimId }).finally(() => {
      setCommentsLoading(false)
    })
  }, [overtimeClaimId, fetchAllOvertimeClaimCommentsByOvertimeClaimId])

  useEffect(() => {
    if (!overtimeClaimId) return

    const fetchHistory = async () => {
      setHistoryLoading(true)
      try {
        const response = await fetchOvertimeClaimAuditHistoryByOvertimeClaimId({ overtimeClaimId })
        setAuditHistory(response)
      } catch (error) {
        console.error('Error fetching audit history:', error)
      } finally {
        setHistoryLoading(false)
      }
    }

    fetchHistory()
  }, [overtimeClaimId, fetchOvertimeClaimAuditHistoryByOvertimeClaimId])

  const mappedComments = (comments || []).map((comment: any) => ({
    id: comment.overtimeClaimCommentId,
    message: comment.comment,
    senderUsername: comment.createdBy,
    timestamp: comment.createdDate
  }))

  const handleSendComment = async (message: string) => {
    if (!overtimeClaimId) return
    const now = new Date().toISOString()
    await createOvertimeClaimComment({
      overtimeClaimId,
      comment: message,
      createdDate: now,
      createdBy: userId,
      updatedDate: now,
      updatedBy: userId
    })
    await fetchAllOvertimeClaimCommentsByOvertimeClaimId({ overtimeClaimId })
  }

  const handleEditComment = async (messageId: string | number, newMessage: string) => {
    const now = new Date().toISOString()
    await updateOvertimeClaimComment({
      overtimeClaimCommentId: messageId,
      overtimeClaimId,
      comment: newMessage,
      updatedDate: now,
      updatedBy: userId
    })
    if (overtimeClaimId) {
      await fetchAllOvertimeClaimCommentsByOvertimeClaimId({ overtimeClaimId })
    }
  }

  const handleDeleteComment = async (messageId: string | number) => {
    await deleteOvertimeClaimComment({ overtimeClaimCommentId: messageId })
    if (overtimeClaimId) {
      await fetchAllOvertimeClaimCommentsByOvertimeClaimId({ overtimeClaimId })
    }
  }

  const updateStatus = async ({ overtimeClaimId, claimStatus }: any) => {
    try {
      const hasClientMatter = overtimeDetails?.clientNo && overtimeDetails?.matterNo
      if (claimStatus === 5 && !hasClientMatter) {
        useToastStore.getState().showToast({
          type: 'warning',
          title: 'Manual posting required',
          text: 'This overtime claim does not have a client/matter number and will need to be posted manually'
        })
      }
      await updateOvertimeClaimStatuses({
        overtimeClaimIds: [overtimeClaimId],
        status: claimStatus,
        claimIdsWithoutClientMatter: hasClientMatter ? [] : [overtimeClaimId]
      })
    } catch (error) {
      console.error('Error updating claim status:', error)
    }
  }

  useEffect(() => {
    switch (claimStatus) {
      case 0:
        setClaimStatusText('This claim is currently in draft status. It has not yet been submitted for review.')
        break
      case 1:
        setClaimStatusText('This claim is currently pending review. It has been submitted and is awaiting action.')
        break
      case 2:
        setClaimStatusText('This claim has been approved. It has been reviewed and accepted.')
        break
      case 3:
        setClaimStatusText('This claim has been rejected. It has been reviewed and declined.')
        break
      case 4:
        setClaimStatusText(
          'This claim has action required. It has been reviewed but requires additional information or correction before it can proceed.'
        )
        break
      case 5:
        setClaimStatusText(
          'This claim is ready to pay. It has been reviewed and approved, and is now awaiting payment processing.'
        )
        break
      case 6:
        setClaimStatusText(
          'This claim has been posted. It has been processed and recorded in the system, but payment has not yet been issued.'
        )
        break
      case 7:
        setClaimStatusText(
          'This claim has been paid. It has been processed and payment has been issued to the claimant.'
        )
        break
      case 8:
        setClaimStatusText(
          'This claim is TOIL approved. It has been reviewed and approved for Time Off in Lieu (TOIL) instead of monetary reimbursement.'
        )
        break
      default:
        setClaimStatusText('Please select a claim status from the dropdown.')
    }
  }, [claimStatus])

  return (
    <MUIStack
      data-testid="OvertimeReviewModule"
      direction="column"
      sx={{
        width: '100%',
        maxWidth: '1600px',
        margin: '0 auto 2rem',
        minHeight: '45rem',
        height: 'fit-content',
        padding: '24px 40px 40px'
      }}
    >
      <MUIButton
        variant="text"
        onClick={() => navigate('/accounts/employee-claims/overtime-claims')}
        sx={{
          color: 'text.secondary',
          textTransform: 'none',
          mb: 4,
          px: 1,
          width: 'fit-content',
          minWidth: 'auto',
          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
        }}
      >
        <ArrowRightIcon
          sx={{ transform: 'rotate(180deg)', height: '18px', width: '18px', color: theme.palette.primary.main, mr: 2 }}
        />
        <MUITypography variant="body1" sx={{ mb: 0, fontWeight: 600, pointerEvents: 'none' }}>
          All overtime claims
        </MUITypography>
      </MUIButton>
      <MUIStack direction="row" sx={{ gap: '1rem', height: 'fit-content' }}>
        <MUIPaper
          sx={{
            alignItems: 'center',
            background: 'white',
            border: '1px solid #D8DCE5',
            borderRadius: 1,
            boxShadow: 'none',
            flex: 4,
            maxHeight: 'fit-content',
            width: '60%'
          }}
        >
          <MUIStack direction="column" sx={{ m: 4, gap: 2, px: 4 }}>
            <MUIStack direction="row" sx={{ gap: 1, alignItems: 'baseline', justifyContent: 'space-between' }}>
              <MUITypography variant="h6">Claim Details</MUITypography>
              <MUITooltip
                arrow
                placement="top"
                title={isEditDisabled ? getDisabledClaimTooltip('edit') : 'Edit claim details'}
              >
                <span>
                  <MUIButton
                    variant="text"
                    size="small"
                    onClick={() => setIsClaimsDrawerOpen(false, { overtimeClaimId: overtimeClaimId }, false)}
                    disabled={isEditDisabled}
                    sx={{
                      borderRadius: 4,
                      backgroundColor: theme.palette.grey[200],
                      width: '1rem',
                      height: '1rem',
                      minWidth: '1rem',
                      padding: 4
                    }}
                  >
                    <EditIcon sx={{ height: '1rem', width: '1rem' }} />
                  </MUIButton>
                </span>
              </MUITooltip>
            </MUIStack>
            <MUIDivider />

            <MUIStack direction="row" justifyContent="space-between" gap={10} flexWrap="wrap" sx={{ py: 4 }}>
              <MUIStack direction="column" sx={{ gap: 10, flex: 1, minWidth: '14rem' }}>
                <MUIStack direction="column">
                  <MUITypography
                    variant="body1"
                    sx={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 600, mb: 0 }}
                  >
                    Date incurred
                  </MUITypography>
                  <MUITypography variant="body2">{formatDate(overtimeDetails?.incurredDate) || '-'}</MUITypography>
                </MUIStack>
                <MUIStack direction="column">
                  <MUITypography
                    variant="body1"
                    sx={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 600, mb: 0 }}
                  >
                    Claim recipient
                  </MUITypography>
                  <MUITypography variant="body2">{overtimeDetails?.claimRecipientUserId || '-'}</MUITypography>
                </MUIStack>
                <MUIStack direction="column">
                  <MUITypography
                    variant="body1"
                    sx={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 600, mb: 0 }}
                  >
                    Associated matter
                  </MUITypography>
                  {overtimeDetails?.clientNo ? (
                    <MUIStack
                      direction="row"
                      alignItems="center"
                      sx={{ gap: 1, maxWidth: '20rem', mt: 2, backgroundColor: '#F8F9FB', borderRadius: 1, p: 2 }}
                    >
                      <Avatar
                        name={
                          matterDetails?.forename && matterDetails?.surname
                            ? `${matterDetails.forename} ${matterDetails.surname}`
                            : matterDetails?.surname || matterDetails?.clientName || ''
                        }
                        sx={{ height: '2rem', width: '2rem', marginRight: '0.5rem' }}
                      />
                      <ClientNameAndMatterDescription
                        clientNo={overtimeDetails.clientNo}
                        matterNo={Number(overtimeDetails.matterNo)}
                        surname={
                          matterDetails?.forename && matterDetails?.surname
                            ? `${matterDetails.forename} ${matterDetails.surname}`
                            : matterDetails?.surname || matterDetails?.clientName || ''
                        }
                        matterDescription={matterDetails?.matterDescription || ''}
                        highlightIfHighRisk={true}
                      />
                    </MUIStack>
                  ) : (
                    <MUITypography variant="body2">-</MUITypography>
                  )}
                </MUIStack>
                <MUIStack direction="column">
                  <MUITypography
                    variant="body1"
                    sx={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 600, mb: 0 }}
                  >
                    Claim description
                  </MUITypography>
                  <MUITypography variant="body2">{overtimeDetails?.description || '-'}</MUITypography>
                </MUIStack>
              </MUIStack>

              <MUIStack direction="column" sx={{ gap: 10, flex: 1, minWidth: '14rem' }}>
                <MUIStack direction="column">
                  <MUITypography
                    variant="body1"
                    sx={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 600, mb: 0 }}
                  >
                    Claim type
                  </MUITypography>
                  <MUITypography variant="body2">
                    {overtimeDetails?.overtimeClaimTypeDescription || overtimeDetails?.overtimeClaimTypeId || '-'}
                  </MUITypography>
                </MUIStack>
                <MUIStack direction="column">
                  <MUITypography
                    variant="body1"
                    sx={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 600, mb: 0 }}
                  >
                    Claim amount
                  </MUITypography>
                  <MUITypography variant="body2">{formatCurrency(overtimeDetails?.amount) || '-'}</MUITypography>
                </MUIStack>
                <MUIStack direction="column">
                  <MUITypography
                    variant="body1"
                    sx={{ textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 600, mb: 0 }}
                  >
                    Overtime hours
                  </MUITypography>
                  <MUITypography variant="body2">{overtimeDetails?.hours ?? '-'}</MUITypography>
                </MUIStack>
              </MUIStack>
            </MUIStack>
          </MUIStack>
        </MUIPaper>

        <MUIStack direction="column" sx={{ flex: 3, gap: '1rem', maxWidth: '30rem' }}>
          <MUIPaper
            sx={{
              background: 'white',
              border: '1px solid #D8DCE5',
              borderRadius: 1,
              boxShadow: 'none'
            }}
          >
            <ClaimStatusDropdownButton
              value={claimStatus}
              onChange={(newStatus) => {
                setClaimStatus(newStatus)
                updateStatus({ overtimeClaimId, claimStatus: newStatus })
              }}
              isExpensesTable={false}
              hiddenStatuses={[0]}
              disabledStatuses={disabledStatuses}
              statusTooltips={statusTooltips}
              sx={{ width: '100%', m: 4, maxWidth: '-webkit-fill-available', height: '3rem' }}
            />
            <MUITypography variant="body2" sx={{ color: '#6B778C', m: 4 }}>
              {claimStatusText}
            </MUITypography>
          </MUIPaper>

          <MUIPaper
            sx={{
              background: 'white',
              border: '1px solid #D8DCE5',
              borderRadius: 1,
              boxShadow: 'none',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: 'fit-content',
              flex: 1
            }}
          >
            <MUITypography variant="h6" sx={{ m: 4, mb: 0 }}>
              Approval notes
            </MUITypography>
            {commentsLoading ? (
              <LoadingState sx={{ flex: 1 }} />
            ) : (
              <Chat
                messages={mappedComments}
                sendHandler={handleSendComment}
                onEditMessage={handleEditComment}
                onDeleteMessage={handleDeleteComment}
              />
            )}
          </MUIPaper>

          <MUIPaper
            sx={{
              background: 'white',
              border: '1px solid #D8DCE5',
              borderRadius: 1,
              boxShadow: 'none',
              overflow: 'auto'
            }}
          >
            <MUITypography variant="h6" sx={{ m: 4, mb: 2, minHeight: 'fit-content', flex: 1 }}>
              History
            </MUITypography>
            {historyLoading ? (
              <LoadingState sx={{ height: '100%' }} />
            ) : (
              <MUIStack spacing={2} sx={{ px: 4, pb: 4 }}>
                {(auditHistory || []).map((item: any, index: number) => (
                  <MUIBox key={index}>
                    <MUITypography variant="body2" sx={{ mb: 0 }}>
                      {item.description}
                    </MUITypography>
                    <MUITypography variant="caption" color="text.secondary">
                      {getRelativeDate(item.createdDate)}
                    </MUITypography>
                  </MUIBox>
                ))}
              </MUIStack>
            )}
          </MUIPaper>
        </MUIStack>
      </MUIStack>
    </MUIStack>
  )
}
